import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addWeeks, subWeeks, isAfter, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen, Calendar, ChevronLeft, ChevronRight, Clock,
  FileText, Users, Check, X, ArrowLeft, GraduationCap,
  MapPin, Award, TrendingUp, CheckCircle2, XCircle,
  AlertTriangle, ClipboardCheck, LogOut, User, CalendarClock, Layers, Eye
} from 'lucide-react';
import { ModulesTab } from './candidat/ModulesTab';
import { SupportsTab } from './candidat/SupportsTab';
import { AlertesBanner } from './candidat/AlertesBanner';
import { SatisfactionPortailPanel } from '../components/SatisfactionPortailPanel';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PortailCandidatPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [parcours, setParcours] = useState(null);
  const [planning, setPlanning] = useState([]);
  const [qcms, setQcms] = useState([]);
  const [modulesData, setModulesData] = useState(null);
  const [validationsData, setValidationsData] = useState(null);
  const [alertesData, setAlertesData] = useState(null);
  const [supportsData, setSupportsData] = useState([]);
  const [supportsSeenTs, setSupportsSeenTs] = useState('');
  const [supportsSeenSnapshot, setSupportsSeenSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('parcours');
  const [currentWeek, setCurrentWeek] = useState(new Date());

  // Admin : voir le portail comme un candidat
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [candidats, setCandidats] = useState([]);
  const [viewAsCandidat, setViewAsCandidat] = useState(null);

  const fetchCandidats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const resp = await axios.get(`${API_URL}/candidats`);
      setCandidats([...resp.data].sort((a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`)));
    } catch (err) { /* silencieux */ }
  }, [isAdmin]);

  const fetchData = useCallback(async () => {
    if (isAdmin && !viewAsCandidat) {
      setParcours(null);
      setPlanning([]);
      setQcms([]);
      setModulesData(null);
      setValidationsData(null);
      setAlertesData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = isAdmin && viewAsCandidat ? `?view_as=${viewAsCandidat.id}` : '';
    try {
      const [parcoursRes, planningRes, qcmsRes, modulesRes, alertesRes, supportsRes, validationsRes] = await Promise.all([
        axios.get(`${API_URL}/portail/candidat/parcours${params}`),
        axios.get(`${API_URL}/portail/candidat/planning${params}`),
        axios.get(`${API_URL}/portail/candidat/qcms${params}`),
        axios.get(`${API_URL}/portail/candidat/modules${params}`),
        axios.get(`${API_URL}/portail/candidat/alertes${params}`),
        axios.get(`${API_URL}/portail/candidat/supports${params}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/portail/candidat/validations${params}`).catch(() => ({ data: { dossiers: [] } })),
      ]);
      setParcours(parcoursRes.data);
      setPlanning(planningRes.data);
      setQcms(qcmsRes.data);
      setModulesData(modulesRes.data);
      setAlertesData(alertesRes.data);
      setSupportsData(supportsRes.data);
      setValidationsData(validationsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Votre compte n\'est pas lié à un profil candidat');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin, viewAsCandidat]);

  useEffect(() => { fetchData(); fetchCandidats(); }, [fetchData, fetchCandidats]);

  // Pastille "Nouveau" sur les supports pédagogiques
  const candKey = parcours?.candidat?.id || viewAsCandidat?.id || user?.id;
  useEffect(() => {
    if (!candKey) return;
    setSupportsSeenTs(localStorage.getItem(`supports_seen_${candKey}`) || '');
    setSupportsSeenSnapshot(null);
  }, [candKey]);
  useEffect(() => {
    if (activeTab !== 'supports' || !candKey) return;
    const prevSeen = localStorage.getItem(`supports_seen_${candKey}`) || '';
    setSupportsSeenSnapshot(prev => prev !== null ? prev : prevSeen);
    const now = new Date().toISOString();
    localStorage.setItem(`supports_seen_${candKey}`, now);
    setSupportsSeenTs(now);
  }, [activeTab, candKey]);
  const newSupportsCount = supportsData
    .flatMap(d => d.modules.flatMap(m => m.supports))
    .filter(s => (s.uploaded_at || '') > supportsSeenTs).length;

  const handleLogout = () => { logout(); navigate('/login'); };

  // Week view helpers
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd }).filter(d => d.getDay() !== 0 && d.getDay() !== 6);

  const getDayEvents = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return planning.filter(e => e.date === dateStr);
  };

  const presenceIcon = (statut) => {
    switch (statut) {
      case 'present': return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
      case 'retard': return <Clock className="w-3.5 h-3.5 text-amber-500" />;
      case 'absent': return <XCircle className="w-3.5 h-3.5 text-red-500" />;
      case 'en_aft': return <GraduationCap className="w-3.5 h-3.5 text-cyan-600" />;
      default: return <AlertTriangle className="w-3.5 h-3.5 text-slate-300" />;
    }
  };

  const presenceLabel = (statut) => {
    switch (statut) {
      case 'present': return 'Present';
      case 'retard': return 'Retard';
      case 'absent': return 'Absent';
      case 'en_aft': return 'Session de certification (AFT)';
      default: return 'Non renseigne';
    }
  };

  if (loading && !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const candidat = parcours?.candidat;
  const dossiers = parcours?.parcours || [];

  // Stats globales
  const totalPresences = dossiers.reduce((s, d) => s + d.presences, 0);
  const totalAbsences = dossiers.reduce((s, d) => s + d.absences, 0);
  const totalRetards = dossiers.reduce((s, d) => s + (d.retards || 0), 0);
  const totalPointages = totalPresences + totalAbsences + totalRetards;
  const tauxPresence = totalPointages > 0 ? Math.round(((totalPresences + totalRetards) / totalPointages) * 100) : null;
  const totalQcm = qcms.length;
  const qcmPasses = qcms.filter(q => q.deja_passe).length;
  const qcmMoyenne = qcmPasses > 0 ? Math.round(qcms.filter(q => q.deja_passe).reduce((s, q) => s + (q.score || 0), 0) / qcmPasses) : 0;
  const progressionGlobale = modulesData?.global?.progression_pct ?? 0;
  const heuresRealisees = modulesData?.global?.heures_realisees ?? 0;
  const heuresPrevues = modulesData?.global?.heures_prevues ?? 0;
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const prochaineSession = planning.find(e => e.date >= todayStr);

  const mainContent = (
    <div className="space-y-6">
        {/* Alertes */}
        <AlertesBanner alertesData={alertesData} />
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-cards">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="kpi-progression">{progressionGlobale}%</p>
                  <p className="text-xs text-slate-500">Progression ({heuresRealisees}h / {heuresPrevues}h)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="kpi-taux-presence">{tauxPresence !== null ? `${tauxPresence}%` : '—'}</p>
                  <p className="text-xs text-slate-500">Taux de présence</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="kpi-qcm">{qcmPasses > 0 ? `${qcmMoyenne}%` : '—'}</p>
                  <p className="text-xs text-slate-500">Moyenne QCM ({qcmPasses}/{totalQcm})</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                  <CalendarClock className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  {prochaineSession ? (
                    <>
                      <p className="text-sm font-bold text-slate-900 capitalize" data-testid="kpi-prochaine-session">
                        {format(parseISO(prochaineSession.date), 'EEE d MMM', { locale: fr })}
                      </p>
                      <p className="text-xs text-slate-500 truncate max-w-[140px]">{prochaineSession.dossier_intitule}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-bold text-slate-900" data-testid="kpi-prochaine-session">—</p>
                      <p className="text-xs text-slate-500">Prochaine session</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
          <TabsList className="bg-white border shadow-sm w-max">
            <TabsTrigger value="parcours" data-testid="tab-parcours">
              <BookOpen className="w-4 h-4 mr-1.5" /> Mon Parcours
            </TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules">
              <Layers className="w-4 h-4 mr-1.5" /> Mes Modules
            </TabsTrigger>
            <TabsTrigger value="planning" data-testid="tab-planning">
              <Calendar className="w-4 h-4 mr-1.5" /> Planning
            </TabsTrigger>
            <TabsTrigger value="supports" data-testid="tab-supports">
              <FileText className="w-4 h-4 mr-1.5" /> Mes Supports
              {newSupportsCount > 0 && (
                <span className="ml-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="badge-new-supports">
                  {newSupportsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="tab-evaluations">
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> Evaluations
            </TabsTrigger>
            <TabsTrigger value="presences" data-testid="tab-presences">
              <Users className="w-4 h-4 mr-1.5" /> Presences
            </TabsTrigger>
          </TabsList>
          </div>

          {/* PARCOURS */}
          <TabsContent value="parcours" className="space-y-4">
            {dossiers.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500">Aucune formation en cours</p>
                </CardContent>
              </Card>
            ) : (
              dossiers.map(d => (
                <Card key={d.dossier_id} className="border-0 shadow-sm overflow-hidden" data-testid={`parcours-${d.dossier_id}`}>
                  <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-5 py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{d.dossier_intitule}</p>
                        <p className="text-indigo-200 text-xs mt-0.5">{d.formation_titre}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {d.parcours_partiel && (
                          <Badge className="bg-amber-500 text-white text-[10px]">Parcours partiel</Badge>
                        )}
                        <Badge className="bg-white/20 text-white text-xs">{d.dossier_code}</Badge>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-5 space-y-4">
                    {/* Progress */}
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-slate-500">Progression</span>
                        <span className="font-semibold text-indigo-600">{d.progression}%</span>
                      </div>
                      <Progress value={d.progression} className="h-2" />
                    </div>

                    {/* Infos grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-slate-400 mb-0.5">Periode</p>
                        <p className="font-medium text-slate-700">
                          {d.date_debut ? format(parseISO(d.date_debut), 'dd/MM/yyyy') : '—'}
                          {d.date_fin ? ` → ${format(parseISO(d.date_fin), 'dd/MM/yyyy')}` : ''}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-slate-400 mb-0.5">Duree</p>
                        <p className="font-medium text-slate-700">{d.total_jours} jour(s) — {d.duree_heures}h</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-slate-400 mb-0.5">Lieu</p>
                        <p className="font-medium text-slate-700">{d.lieu_principal || '—'}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-slate-400 mb-0.5">Evaluations</p>
                        <p className="font-medium text-slate-700">{d.qcm_passes} QCM passes{d.qcm_moyenne > 0 ? ` (${d.qcm_moyenne}%)` : ''}</p>
                      </div>
                    </div>

                    {/* Attendance summary */}
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1 text-emerald-600">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {d.presences} present(s)
                      </span>
                      {d.retards > 0 && (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Clock className="w-3.5 h-3.5" /> {d.retards} retard(s)
                        </span>
                      )}
                      {d.absences > 0 && (
                        <span className="flex items-center gap-1 text-red-600">
                          <XCircle className="w-3.5 h-3.5" /> {d.absences} absence(s)
                        </span>
                      )}
                    </div>

                    <SatisfactionPortailPanel portail="candidat" dossierId={d.dossier_id} viewAs={isAdmin && viewAsCandidat ? viewAsCandidat.id : undefined} />
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* MODULES */}
          <TabsContent value="modules">
            <ModulesTab modulesData={modulesData} validationsData={validationsData} />
          </TabsContent>

          {/* PLANNING */}
          <TabsContent value="planning">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <Button variant="outline" size="sm" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} aria-label="Semaine précédente" data-testid="candidat-week-prev">
                    <ChevronLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Sem. prec.</span>
                  </Button>
                  <h2 className="text-xs sm:text-sm font-semibold text-slate-700 text-center">
                    Semaine du {format(weekStart, 'd MMM', { locale: fr })} au {format(weekEnd, 'd MMM yyyy', { locale: fr })}
                  </h2>
                  <Button variant="outline" size="sm" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} aria-label="Semaine suivante" data-testid="candidat-week-next">
                    <span className="hidden sm:inline">Sem. suiv.</span> <ChevronRight className="w-4 h-4 sm:ml-1" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="planning-week-grid">
                  {weekDays.map(day => {
                    const events = getDayEvents(day);
                    const isToday = isSameDay(day, new Date());
                    const isPast = isBefore(day, new Date()) && !isToday;
                    return (
                      <div
                        key={format(day, 'yyyy-MM-dd')}
                        className={`border rounded-lg p-3 min-h-[140px] transition-colors ${
                          isToday ? 'border-indigo-400 bg-indigo-50/50' :
                          isPast ? 'bg-slate-50/50 border-slate-200' :
                          'border-slate-200 bg-white'
                        }`}
                      >
                        <div className={`text-xs font-medium mb-2 ${isToday ? 'text-indigo-700' : 'text-slate-500'}`}>
                          <span className="capitalize">{format(day, 'EEEE', { locale: fr })}</span>
                          <span className="ml-1 font-bold">{format(day, 'd')}</span>
                        </div>
                        <div className="space-y-1.5">
                          {events.map((ev) => (
                            <div
                              key={`${ev.dossier_id}-${ev.date}`}
                              className={`rounded px-2 py-1.5 text-[11px] ${
                                ev.is_stage ? 'bg-violet-100 text-violet-800' :
                                'bg-indigo-100 text-indigo-800'
                              }`}
                            >
                              <div className="font-medium truncate">{ev.dossier_intitule}</div>
                              {ev.module && <div className="text-[10px] opacity-75 truncate mt-0.5">{ev.module}</div>}
                              {ev.lieu && (
                                <div className="flex items-center gap-0.5 mt-0.5 text-[10px] opacity-60">
                                  <MapPin className="w-2.5 h-2.5" /> {ev.lieu}
                                </div>
                              )}
                              <div className="flex items-center gap-1 mt-1">
                                {presenceIcon(ev.presence)}
                                <span className="text-[10px]">{presenceLabel(ev.presence)}</span>
                              </div>
                            </div>
                          ))}
                          {events.length === 0 && (
                            <p className="text-[10px] text-slate-300 text-center mt-6">Libre</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Full list */}
            <Card className="border-0 shadow-sm mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Toutes les sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="planning-table">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                        <th className="px-3 py-2 text-left font-medium">Date</th>
                        <th className="px-3 py-2 text-left font-medium">Formation</th>
                        <th className="px-3 py-2 text-left font-medium">Module</th>
                        <th className="px-3 py-2 text-left font-medium">Lieu</th>
                        <th className="px-3 py-2 text-center font-medium">Presence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {planning.map((ev) => {
                        const d = new Date(ev.date + 'T00:00:00');
                        const isPast = isBefore(d, new Date());
                        return (
                          <tr key={`${ev.dossier_id}-${ev.date}`} className={`${ev.is_stage ? 'bg-violet-50/50' : isPast ? 'bg-slate-50/30' : ''}`}>
                            <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap capitalize">
                              {format(d, 'EEE d MMM yyyy', { locale: fr })}
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              {ev.dossier_intitule}
                            </td>
                            <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">
                              {ev.is_stage ? <Badge variant="outline" className="text-[10px] text-violet-700">Stage</Badge> : (ev.module || '—')}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{ev.lieu || '—'}</td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {presenceIcon(ev.presence)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {planning.length === 0 && (
                        <tr><td colSpan={5} className="text-center py-8 text-slate-400">Aucune session planifiee</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUPPORTS */}
          <TabsContent value="supports">
            <SupportsTab data={supportsData} lastSeen={supportsSeenSnapshot} />
          </TabsContent>

          {/* EVALUATIONS */}
          <TabsContent value="evaluations" className="space-y-4">
            {qcms.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <ClipboardCheck className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500">Aucune evaluation disponible</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* QCMs a passer */}
                {qcms.filter(q => !q.deja_passe).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" /> A passer
                    </h3>
                    <div className="space-y-2">
                      {qcms.filter(q => !q.deja_passe).map(q => (
                        <Card key={`${q.qcm_id}-${q.dossier_id}`} className="border-0 shadow-sm" data-testid={`qcm-todo-${q.qcm_id}`}>
                          <CardContent className="p-4 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-800">{q.titre}{q.nouvelle_tentative && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid={`qcm-nouvelle-tentative-${q.qcm_id}`}>Nouvelle tentative autorisée · précédent {q.score != null ? `${Math.round(q.score)} %` : '—'}</span>}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {q.dossier_intitule} {q.module && `— ${q.module}`}
                              </p>
                              <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                                <span>{q.nb_questions} questions</span>
                                {q.temps_limite && <span>{q.temps_limite} min</span>}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/qcm/public?qcm=${q.qcm_id}&dossier=${q.dossier_id}`, '_blank')}
                              data-testid={`btn-passer-qcm-${q.qcm_id}`}
                            >
                              Passer le QCM
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* QCMs deja passes */}
                {qcms.filter(q => q.deja_passe).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Completees
                    </h3>
                    <div className="space-y-2">
                      {qcms.filter(q => q.deja_passe).map(q => (
                        <Card key={`${q.qcm_id}-${q.dossier_id}`} className="border-0 shadow-sm" data-testid={`qcm-done-${q.qcm_id}`}>
                          <CardContent className="p-4 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-800">{q.titre}{q.nouvelle_tentative && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid={`qcm-nouvelle-tentative-${q.qcm_id}`}>Nouvelle tentative autorisée · précédent {q.score != null ? `${Math.round(q.score)} %` : '—'}</span>}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {q.dossier_intitule} {q.module && `— ${q.module}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className={`text-lg font-bold ${q.score >= 70 ? 'text-emerald-600' : q.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {q.score}%
                                </p>
                                {q.reussi !== null && (
                                  <p className={`text-[10px] font-semibold ${q.reussi ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {q.reussi ? 'Reussi' : 'Non reussi'}
                                  </p>
                                )}
                              </div>
                              {q.reussi ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* PRESENCES */}
          <TabsContent value="presences">
            {dossiers.map(d => (
              <Card key={d.dossier_id} className="border-0 shadow-sm mb-4" data-testid={`presences-${d.dossier_id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>{d.dossier_intitule}</span>
                    <Badge variant="outline" className="text-xs">{d.dossier_code}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Summary */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-4 text-xs">
                    <span className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" /> {d.presences} present(s)
                    </span>
                    <span className="flex items-center gap-1 px-2 py-1 rounded bg-amber-50 text-amber-700">
                      <Clock className="w-3 h-3" /> {d.retards} retard(s)
                    </span>
                    <span className="flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-700">
                      <XCircle className="w-3 h-3" /> {d.absences} absence(s)
                    </span>
                    <span className="text-slate-400 sm:ml-auto w-full sm:w-auto">
                      {d.jours_passes}/{d.total_jours} jours ecoules
                    </span>
                  </div>

                  {/* Calendar visual */}
                  <div className="flex flex-wrap gap-1" data-testid={`presences-calendar-${d.dossier_id}`}>
                    {(d.dates_planning || []).map(dt => {
                      const isPast = dt <= format(new Date(), 'yyyy-MM-dd');
                      // We'll need to find the presence for each day from planning data
                      const planningEvent = planning.find(e => e.date === dt && e.dossier_id === d.dossier_id);
                      const presence = planningEvent?.presence || 'non_renseigne';
                      const bgColor = !isPast ? 'bg-slate-100 border-slate-200' :
                        presence === 'present' ? 'bg-emerald-100 border-emerald-300' :
                        presence === 'retard' ? 'bg-amber-100 border-amber-300' :
                        presence === 'absent' ? 'bg-red-100 border-red-300' :
                        presence === 'en_aft' ? 'bg-cyan-100 border-cyan-300' :
                        'bg-slate-100 border-slate-200';
                      return (
                        <div
                          key={dt}
                          title={`${format(parseISO(dt), 'dd/MM/yyyy')} — ${presenceLabel(presence)}`}
                          className={`w-7 h-7 rounded border flex items-center justify-center text-[9px] font-medium ${bgColor}`}
                        >
                          {format(parseISO(dt), 'd')}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
    </div>
  );

  // Vue admin : sélecteur de candidat + même visuel que le candidat
  if (isAdmin) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="portail-candidat-admin">
          <Card className="border-indigo-200 bg-indigo-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                  <Eye className="w-4 h-4" />
                  Vue admin — Sélectionnez un candidat :
                </div>
                <Select
                  value={viewAsCandidat?.id || 'none'}
                  onValueChange={(v) => {
                    if (v === 'none') {
                      setViewAsCandidat(null);
                    } else {
                      setViewAsCandidat(candidats.find(c => c.id === v));
                    }
                    setActiveTab('parcours');
                  }}
                >
                  <SelectTrigger className="w-72 bg-white" data-testid="select-candidat-view">
                    <SelectValue placeholder="Choisir un candidat..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Aucun candidat --</SelectItem>
                    {candidats.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nom} {c.prenom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {viewAsCandidat && (
                  <Badge className="bg-indigo-600 text-white" data-testid="badge-candidat-view">
                    {viewAsCandidat.nom} {viewAsCandidat.prenom}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {!viewAsCandidat ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center" data-testid="admin-no-candidat">
                <GraduationCap className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">Sélectionnez un candidat pour visualiser son espace formation</p>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
            </div>
          ) : (
            mainContent
          )}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portail-candidat">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans'] truncate">Mon Espace Formation</h1>
              <p className="text-xs text-slate-500 truncate">{candidat?.nom} {candidat?.prenom}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="btn-logout" className="shrink-0" aria-label="Déconnexion">
            <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Deconnexion</span>
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {mainContent}
      </div>
    </div>
  );
};
